import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AuthenticationService, LocalizationService, NotifyService } from '@app/core';
import { Credentials, Language, SmsRequestContext, SuperAdminLoginContext } from '@app/shared/common/interfaces';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-super-admin-login-dialog',
    templateUrl: './super-admin-login-dialog.component.html',
    styleUrls: ['./super-admin-login-dialog.component.scss']
})
export class SuperAdminLoginDialogComponent implements OnInit, OnDestroy {
    @Input() visible: boolean = false;
    @Input() email: string;
    @Input() password: string;
    @Input() remember: boolean;
    @Input() selectedLanguage: Language;
    @Input() encryptedInvitationID: string;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() loginSuccess = new EventEmitter<Credentials>();
    @Output() loginCancel = new EventEmitter<void>();

    superAdminForm: FormGroup;
    otpSent: boolean = false;
    otpTimer: number = 300;
    otpTimerInterval: any;
    isOtpVerifying: boolean = false;
    isOtpValidated: boolean = false;

    otpCodeConfig: any = {
        allowNumbersOnly: true,
        length: 6,
        isPasswordInput: false,
        disableAutoFocus: false,
        placeholder: ''
    };

    @ViewChild('otpInput') otpInput: any;

    constructor(
        private authenticationService: AuthenticationService,
        private localizationService: LocalizationService,
        private notifyService: NotifyService
    ) {}

    ngOnInit() {
        this.initForm();
    }

    ngOnDestroy() {
        if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
        }
    }

    initForm() {
        this.superAdminForm = new FormGroup({
            phoneNumber: new FormControl('', {
                validators: [
                    Validators.required,
                    this.trimValidator,
                    Validators.pattern('^[0-9+() -]+$'),
                    Validators.minLength(13)
                ],
                updateOn: 'change'
            }),
            otpCode: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(6),
                    Validators.pattern('^[0-9]{6}$')
                ],
                updateOn: 'change'
            })
        });
    }

    trimValidator: ValidatorFn = (control: FormControl) => {
        if (control.value !== null && control.value.startsWith(' ')) {
            return {
                error: { value: 'Key.StartsWithErr' },
            };
        }
        if (control.value !== null && control.value.endsWith(' ')) {
            return {
                error: { value: 'Key.EndsWithErr' },
            };
        }
        return null;
    };

    checkSuperAdminFormValidity(formControlName: string): boolean {
        const control = this.superAdminForm.get(formControlName);
        return control && control.touched && control.invalid;
    }

    getSuperAdminFormValidityMessage(formControlName: string): string {
        const control = this.superAdminForm.get(formControlName);
        if (!control || !control.touched || !control.errors) return '';
        
        if (control.errors?.['error']) {
            return this.localizationService.translateTextWithoutKey(
                control.errors?.['error'].value
            );
        }
        
        if (control.errors?.['required']) {
            return this.localizationService.translateText(
                formControlName === 'phoneNumber' ? 'PhoneNumberRequired' : 'OtpCodeRequired'
            );
        }

        if (control.errors?.['pattern']) {
            return this.localizationService.translateText(
                formControlName === 'phoneNumber' ? 'InvalidPhoneNumber' : 'InvalidOtpCode'
            );
        }

        if (control.errors?.['minLength']) {
            return this.localizationService.translateText('InvalidLength');
        }
        
        return '';
    }

    onOtpChange(otpValue: string) {
        this.superAdminForm.patchValue({ otpCode: otpValue });
        this.isOtpValidated = false;
    }

    sendOtpCode() {
        const phoneControl = this.superAdminForm.get('phoneNumber');
        phoneControl.markAsTouched();
        
        if (phoneControl.invalid) {
            return;
        }

        const smsContext: SmsRequestContext = {
            phoneNumber: phoneControl.value.trim(),
            langId: this.selectedLanguage.id
        };

        lastValueFrom(this.authenticationService.sendSuperAdminOTPSms(smsContext))
            .then((response) => {
                this.otpSent = true;
                this.startOtpTimer();
                this.notifyService.notifySuccess(
                    this.localizationService.translateText('OtpCodeSent')
                );
            })
            .catch((error) => {
            });
    }

    startOtpTimer() {
        this.otpTimer = 300;
        if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
        }
        this.otpTimerInterval = setInterval(() => {
            this.otpTimer--;
            if (this.otpTimer <= 0) {
                clearInterval(this.otpTimerInterval);
            }
        }, 1000);
    }

    getDynamicTimerStyle() {
        if (this.otpTimer < 60) {
            return { 'color': '#ef4444', 'font-weight': 'bold' };
        }
        return { 'color': '#6b7280' };
    }

    verifySuperAdminLogin() {
        const otpControl = this.superAdminForm.get('otpCode');
        otpControl.markAsTouched();
        
        if (this.superAdminForm.invalid) {
            this.notifyService.notifyError(
                this.localizationService.translateText('PleaseCheckFormFields')
            );
            return;
        }

        this.isOtpVerifying = true;
        
        const superAdminLoginContext: SuperAdminLoginContext = {
            email: this.email?.trim(),
            password: this.password,
            remember: this.remember,
            language: this.selectedLanguage.id,
            languageCode: this.selectedLanguage.languageCode,
            encryptedInvitationID: this.encryptedInvitationID,
            otpCode: this.superAdminForm.get('otpCode').value,
            phoneNumber: this.superAdminForm.get('phoneNumber').value
        };

        const superAdminLogin$ = this.authenticationService.superAdminLogin(superAdminLoginContext);

        lastValueFrom(superAdminLogin$)
            .then((data: Credentials) => {
                this.isOtpValidated = true;
                
                setTimeout(() => {
                    this.closeDialog();
                    this.loginSuccess.emit(data);
                }, 500);
            })
            .catch((error) => {
                this.isOtpValidated = false;
                this.superAdminForm.get('otpCode').reset();
                if (this.otpInput) {
                    this.otpInput.setValue('');
                }
            })
            .finally(() => {
                this.isOtpVerifying = false;
            });
    }

    resendOtpCode() {
        if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
        }
        this.superAdminForm.get('otpCode').reset();
        this.isOtpValidated = false;
        if (this.otpInput) {
            this.otpInput.setValue('');
        }
        this.sendOtpCode();
    }

    closeDialog() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.resetDialog();
        this.loginCancel.emit();
    }

    resetDialog() {
        this.superAdminForm.reset();
        this.otpSent = false;
        this.otpTimer = 300;
        this.isOtpVerifying = false;
        this.isOtpValidated = false;
        if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
        }
        if (this.otpInput) {
            this.otpInput.setValue('');
        }
    }
}